import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import AIAgentNetwork from "./AIAgentNetwork";
import SplitTextReveal from "./SplitTextReveal";
import MagneticButton from "./MagneticButton";
import { heroData as defaultHeroData, brand } from "../mock";

export default function Hero() {
  const [heroData, setHeroData] = useState(defaultHeroData);
  useEffect(() => {
    fetch("/api/content/hero")
      .then((r) => r.ok ? r.json() : null)
      .then((json) => { if (json?.data) setHeroData(json.data); })
      .catch(() => {});
  }, []);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % (heroData.rotating?.length || 1)), 2400);
    return () => clearInterval(id);
  }, [heroData.rotating?.length]);

  const fade = {
    hidden: { opacity: 0, y: 20 },
    show: (i = 0) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.8, delay: i * 0.08 + 0.2, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section id="home" className="relative pt-32 pb-8 lg:pt-36 lg:pb-10 overflow-hidden">
      {/* Subtle animated line grid behind text */}
      <div className="absolute inset-0 line-grid opacity-30 pointer-events-none animate-grid-fade" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* TEXT COLUMN */}
          <div className="lg:col-span-7 relative z-10 order-2 lg:order-1">
            <motion.div
              initial="hidden" animate="show" variants={fade} custom={0}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-7"
            >
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-[#73100D] animate-ping-soft" />
                <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-[#73100D]" />
              </span>
              {heroData.tagline}
            </motion.div>

            <h1 className="text-[44px] md:text-6xl lg:text-[72px] font-bold leading-[1.02] tracking-tight text-[#111]">
              <SplitTextReveal text={heroData.title} delay={0.15} />
              <br />
              <span className="relative inline-block">
                <SplitTextReveal text={heroData.highlight} delay={0.6} highlight />
                <motion.span
                  aria-hidden
                  initial={{ scaleX: 0, originX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.0, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-1 left-0 right-0 h-[6px] bg-gradient-to-r from-[#73100D]/40 via-[#98413F]/60 to-transparent rounded-full"
                />
              </span>
            </h1>

            <motion.div
              initial="hidden" animate="show" variants={fade} custom={6}
              className="mt-6 flex flex-wrap items-center gap-3 text-[15px] text-[#444]"
            >
              <span className="font-medium text-[#111]">Specialists in</span>
              <span className="relative inline-block min-w-[14ch] h-[1.5em]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={heroData.rotating[idx]}
                    initial={{ y: 14, opacity: 0, filter: "blur(4px)" }}
                    animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                    exit={{ y: -14, opacity: 0, filter: "blur(4px)" }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-0 top-0 font-semibold text-[#73100D]"
                  >
                    {heroData.rotating[idx]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 text-xs font-medium text-[#666]">
                <span className="w-1 h-1 rounded-full bg-[#73100D] animate-ping-soft" />
                Live
              </span>
            </motion.div>

            <motion.p
              initial="hidden" animate="show" variants={fade} custom={7}
              className="mt-7 text-base md:text-lg text-[#555] max-w-xl leading-relaxed"
            >
              {heroData.description}
            </motion.p>

            <motion.div
              initial="hidden" animate="show" variants={fade} custom={8}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <MagneticButton
                href="#contact"
                className="btn-primary inline-flex items-center justify-center rounded-full font-semibold px-7 h-12"
              >
                <span className="inline-flex items-center gap-2">
                  {heroData.primaryCta} <ArrowRight className="w-4 h-4" />
                </span>
              </MagneticButton>
              <MagneticButton
                href="#ai"
                strength={10}
                className="btn-outline-brand inline-flex items-center justify-center rounded-full h-12 px-6 font-medium"
              >
                {heroData.secondaryCta}
              </MagneticButton>
            </motion.div>

            <motion.div
              initial="hidden" animate="show" variants={fade} custom={9}
              className="mt-12 grid grid-cols-3 max-w-lg gap-6 pt-8 border-t border-[#E5E5E5]"
            >
              {heroData.trust.map((s, i) => (
                <motion.div
                  key={s.v}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 + i * 0.1, duration: 0.7 }}
                >
                  <div className="text-2xl md:text-3xl font-semibold text-[#111]">{s.k}</div>
                  <div className="text-[11px] text-[#666] uppercase tracking-[0.18em] mt-1">{s.v}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* VISUAL COLUMN — AI Agent Network (visible on mobile too) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotateY: -8 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative order-1 lg:order-2 w-full"
            style={{ perspective: 1000 }}
          >
            <div className="relative aspect-square w-full max-w-[360px] sm:max-w-[420px] lg:max-w-[500px] mx-auto lg:ml-auto lg:mr-0">
              <div className="absolute -inset-6 rounded-[36px] bg-gradient-to-br from-[#73100D]/12 via-transparent to-[#98413F]/10 blur-2xl" />

              <div className="relative h-full rounded-[28px] overflow-hidden border border-[#E5E5E5] bg-gradient-to-br from-white via-[#fbf7f7] to-[#f5eeee] soft-shadow">
                <div className="absolute inset-0 dot-pattern opacity-40" />

                {/* Corner brackets */}
                <Bracket className="top-3 left-3" />
                <Bracket className="top-3 right-3 scale-x-[-1]" />
                <Bracket className="bottom-3 left-3 scale-y-[-1]" />
                <Bracket className="bottom-3 right-3 scale-x-[-1] scale-y-[-1]" />

                {/* Status badge top-left */}
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 }}
                  className="absolute top-5 left-5 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-[#E5E5E5] text-[10px] font-mono uppercase tracking-[0.15em] text-[#73100D] z-10"
                >
                  <span className="relative inline-flex w-1.5 h-1.5">
                    <span className="absolute inset-0 rounded-full bg-[#73100D] animate-ping-soft" />
                    <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-[#73100D]" />
                  </span>
                  AGENT · ONLINE
                </motion.div>

                {/* Action counter top-right */}
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.1 }}
                  className="absolute top-5 right-5 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur border border-[#E5E5E5] text-[10px] font-mono text-[#555]"
                >
                  <ActionsTicker />
                </motion.div>

                {/* Agent Network SVG */}
                <AIAgentNetwork />

                {/* Bottom log panel */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 }}
                  className="absolute bottom-5 left-5 right-5 rounded-xl bg-white/92 backdrop-blur border border-[#E5E5E5] px-3 py-2.5 soft-shadow"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] font-semibold text-[#73100D]">Activity Stream</div>
                    <div className="text-[9px] font-mono text-[#888]">v3.2.1</div>
                  </div>
                  <ActivityLog />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
        className="flex flex-col items-center gap-2 mt-16 text-[#73100D]/70 text-[10px] tracking-[0.4em] uppercase"
      >
        Scroll
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}
          className="w-px h-8 bg-gradient-to-b from-[#73100D] to-transparent" />
      </motion.div>
    </section>
  );
}

function Bracket({ className = "" }) {
  return (
    <svg className={`absolute w-5 h-5 text-[#73100D]/40 z-10 ${className}`} viewBox="0 0 20 20" fill="none">
      <path d="M2 8 V2 H8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ActionsTicker() {
  const [v, setV] = useState(3427);
  useEffect(() => {
    const id = setInterval(() => setV((x) => x + Math.floor(Math.random() * 4) + 1), 1500);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      <span className="tabular-nums text-[#111] font-semibold">{v.toLocaleString()}</span>
      <span className="text-[#888]">actions today</span>
    </>
  );
}

const LOG_ENTRIES = [
  { src: "Gmail",    msg: "Triage → routed to sales" },
  { src: "Slack",    msg: "Summarized #support" },
  { src: "HubSpot",  msg: "Updated 4 deal stages" },
  { src: "GCal",     msg: "Booked discovery call" },
  { src: "Postgres", msg: "Synced 128 records" },
  { src: "Webhook",  msg: "Triggered Stripe refund" },
];

function ActivityLog() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % LOG_ENTRIES.length), 2200);
    return () => clearInterval(id);
  }, []);
  const e = LOG_ENTRIES[i];
  return (
    <div className="h-5 relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0 flex items-center gap-2 text-[11px]"
        >
          <span className="px-1.5 py-0.5 rounded bg-[#73100D]/10 text-[#73100D] font-mono font-semibold text-[9px] uppercase tracking-wider">
            {e.src}
          </span>
          <span className="text-[#333] truncate">{e.msg}</span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
